<template>
  <PbBasicView no-min-height>
    <BrandsField
      v-model="brand"
      class="pb-half-margin-bottom"
      alignment="right"
    />
    <PbInput
      v-model="name"
      dynamic
      :label="translate('profile.properties.name')"
      class="pb-half-margin-bottom"
    />
    <MessageBox
      v-if="source === 'import'"
      :color="level"
    >
      <FlexBox direction="row">
        <div>
          <p>
            {{ text }}
          </p>
          <p v-if="report">
            {{ translate('pb2.createView.reviewReportMessage') }}
          </p>
          <p v-if="report">
            {{ translate('pb2.createView.reportUnavailableMessage') }}
          </p>
        </div>
        <BaseButton
          v-if="report"
          class="pb-profile-basic-data-toggle-report"
          @click="toggle"
        >
          {{ translate(treeState ? 'pb2.reportDialog.collapseAll' : 'pb2.reportDialog.expandAll') }}
        </BaseButton>
      </FlexBox>
    </MessageBox>
    <div
      v-if="report"
      class="pb-half-margin-top"
    >
      <ReportMessage
        :item="{
          report,
          link: {
            name: routeNames.Content,
            params: {
              profileUid: profile.uid,
            },
          },
          linkName: profile.name,
          data: { _type: 'ssl.profile' },
        }"
        :default-state="treeState"
        :children-state="treeState"
      />
    </div>
    <FlexBox direction="row">
      <ControlButton
        :to="{ name: routeNames.Home }"
        :disabled="loading"
        :tooltip="translate('pb2.createView.backTooltip')"
        tooltip-location="bottom"
        class="pb-create-profile-link"
        :aria-label="translate('pb2.createView.backTooltip')"
        :label="translate('pb2.createView.backLabel')"
      />
      <ButtonWithLoader
        :is-loading="loading"
        class="btn-success"
        tooltip-location="bottom"
        :tooltip="report ? translate('pb2.createView.ignoreAndContinueTooltip') : translate('pb2.createView.continueTooltip')"
        :aria-label="report ? translate('pb2.createView.ignoreAndContinueTooltip') : translate('pb2.createView.continueTooltip')"
        :label="report ? translate('pb2.createView.ignoreAndContinueLabel') : translate('pb2.createView.continueLabel')"
        :disabled="!name"
        @click="confirm"
      />
    </FlexBox>
  </PbBasicView>
</template>

<script setup>
import {
  watch,
  ref,
  toRef,
  computed,
} from 'vue'
import useHotkeys, { KEYS } from '@nicolaudie-group/ng-ui-kit/composables/useHotkeys'
import ControlButton from '@nicolaudie-group/ng-ui-kit/components/ControlButton/ControlButton.vue'
import FlexBox from '@nicolaudie-group/ng-ui-kit/components/FlexBox/FlexBox.vue'
import BrandsField from '@/components/BrandsField/BrandsField.vue'
import PbBasicView from '@/components/PbBasicView.vue'
import PbInput from '@/components/PbInput.vue'
import ButtonWithLoader from '@/components/ButtonWithLoader.vue'
import ReportMessage from '@/components/ReportMessage/ReportMessage.vue'
import routeNames from '@/router/names'
import useProfileStore from '@/stores/profile.store'
import useRouterStore from '@/stores/router.store'
import { useProfile } from '@/composables/useProfile'
import useTranslate from '@/composables/useTranslate'
import useToggle from '@nicolaudie-group/ng-ui-kit/composables/useToggle'
import MessageBox from '@nicolaudie-group/ng-ui-kit/components/MessageBox/MessageBox.vue'
import BaseButton from '@nicolaudie-group/ng-ui-kit/components/BaseButton/BaseButton.vue'
import useReportBadge from '../../composables/useReportBadge'

const { translate } = useTranslate()

const profile = useProfile()

const routerStore = useRouterStore()

const name = ref('')
const brand = ref('_Generic')

// Can be: "new", "import", "copy"
const source = computed(() => routerStore.route.params?.source || 'new')

// Manually bind a loading value to the button, so that:
// - the button gets the loading state also when using enter hotkey
// - we can disable creation while the request is pending
const loading = ref(false)

watch(profile, () => {
  if (source.value === 'import' || source.value === 'copy') {
    name.value = routerStore.route.query?.name || profile.value?.name || name.value
    brand.value = routerStore.route.query?.brand || profile.value?.brand || brand.value
  } else if (source.value === 'new') {
    name.value = routerStore.route.query?.name || name.value
    brand.value = routerStore.route.query?.brand || brand.value
  }
}, { immediate: true })

const profileStore = useProfileStore()

async function confirm() {
  if (!loading.value) {
    loading.value = true

    const payload = {
      name: name.value,
      brand: brand.value,
    }

    let uid
    if (source.value === 'new') {
      // If creating a new profile (not imported), create with the provided brand & name
      const result = await profileStore.create(payload)
      uid = result.uid
    } else if (source.value === 'import' || source.value === 'copy') {
      uid = profile.value?.uid
      // If there already is a profile (imported or copied)
      const nameChanged = name.value !== profile.value?.name
      const brandChanged = brand.value !== profile.value?.brand
      if (uid && (nameChanged || brandChanged)) {
        // If the name or brand changed, edit the profile
        await profileStore.edit(payload)
      }
    }

    if (uid) {
      // Redirect to profile properties once done
      await routerStore.push({
        name: routeNames.Properties,
        params: {
          profileUid: uid,
        },
      })
    }

    loading.value = false
  }
}

const { active: enterPressed } = useHotkeys(KEYS.Enter, {
  shift: false,
  alt: false,
  meta: false,
  ctrl: false,
  exact: true,
})

watch(enterPressed, () => {
  if (enterPressed.value) {
    confirm()
  }
})

const report = toRef(profileStore, 'importReport')
const {
  level,
  errorsCount,
  warningsCount,
  infosCount,
  errorText,
  warningText,
  infoText,
} = useReportBadge(report)

const text = computed(() => {
  const errors = errorsCount.value
  const warnings = warningsCount.value
  const infos = infosCount.value

  const errorTxt = errorText.value
  const warningTxt = warningText.value
  const infoTxt = infoText.value

  const result = []

  if (errors) {
    result.push(errorTxt)
  }
  if (warnings) {
    result.push(warningTxt)
  }
  if (infos) {
    result.push(infoTxt)
  }

  if (result.length) {
    const translation = translate('pb2.createView.reportMessage')
    const last = result.splice(result.length - 1, 1)
    return `${result.join(', ')}${result.length ? ` ${translation.and} ` : ''}${last} ${translation.wereRaised}`
  }

  return translate('pb2.createView.importSuccessfulMessage')
})

const { state: treeState, toggle } = useToggle(true)
</script>

<style lang="scss">
@use '@nicolaudie-group/ng-ui-kit/defines/_fonts.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_colors.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_config.define.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_flexbox.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_generic.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_responsive.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_theme.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_animations.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_transitions.mixin.scss';

@use '/src/styles/_pb.scss';

.pb-profile-basic-data-toggle-report {
  text-decoration: underline;
  flex-shrink: 0;

  &:hover {
    color: colors.$color-default--black;
  }
}
</style>
